import { useQuery, useQueryClient } from '@tanstack/react-query';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useState } from 'react';
import { toast } from 'sonner';

import type { AccessTier, GatePolicy } from '@/features/composer/types';

import { CategoryPicker } from '@/features/catalog/CategoryPicker';
import { ArticleComposer } from '@/features/composer/ArticleComposer';
import { DISPLAY, EMPTY_DOC, TOPICS, TRADITIONS, type ArticleDoc } from '@/features/composer/types';
import { useRequireRole } from '@/lib/auth/useRequireRole';
import { catalogMutations } from '@/lib/queries/catalog';
import { contentQueries, type Content } from '@/lib/queries/content';
import { supabase } from '@/lib/supabase';

export const Route = createFileRoute('/creator/content/$contentId/edit')({
  component: EditRoute,
});

function EditRoute() {
  const { loading } = useRequireRole(['creator'], { creatorStatus: 'approved' });
  const { contentId } = Route.useParams();
  const content = useQuery(contentQueries.byId(contentId));

  if (loading || content.isLoading) {
    return <p className="font-sans text-sm text-ink/50">Loading…</p>;
  }
  const c = content.data;
  if (!c) {
    return (
      <p className="font-sans text-sm text-ink/70">
        That piece couldn&apos;t be found, or you don&apos;t have access to it.
      </p>
    );
  }

  if (c.type === 'video') {
    return <VideoEditForm content={c} />;
  }

  return (
    <ArticleComposer
      initialContentId={c.id}
      initialStatus={c.status}
      reviewerNotes={c.reviewer_notes}
      initial={{
        title: c.title,
        excerpt: c.excerpt ?? '',
        slug: c.slug,
        bodyJson: (c.body_json as ArticleDoc | null) ?? EMPTY_DOC,
        coverAssetId: c.cover_asset_id ?? null,
        coverImageUrl: c.cover_image_url ?? '',
        coverImageAlt: c.cover_image_alt ?? '',
        traditionTags: c.tradition_tags ?? [],
        topicTags: c.topic_tags ?? [],
        gatePolicy: (c.gate_policy as GatePolicy) ?? 'free_14_days',
        accessTier: (c.access_tier as AccessTier) ?? 'metered',
      }}
    />
  );
}

function VideoEditForm({ content }: { content: Content }) {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const status = content.status;
  const [meta, setMeta] = useState({
    title: content.title,
    excerpt: content.excerpt ?? '',
    traditionTags: content.tradition_tags ?? [],
    topicTags: content.topic_tags ?? [],
    categoryId: content.category_id ?? null,
  });
  const [saving, setSaving] = useState(false);

  if (status === 'pending_review') {
    return (
      <Note title={content.title}>
        This video is in review. Withdraw it from review first to edit its details.
      </Note>
    );
  }
  if (status === 'published') {
    return (
      <Note title={content.title}>
        Published pieces are managed through editorial. Contact the team to revise or unpublish this
        video.
      </Note>
    );
  }

  const toggle = (key: 'traditionTags' | 'topicTags', value: string) =>
    setMeta((m) => ({
      ...m,
      [key]: m[key].includes(value) ? m[key].filter((t) => t !== value) : [...m[key], value],
    }));

  const save = async (resubmit: boolean) => {
    if (
      resubmit &&
      (!meta.title || meta.traditionTags.length === 0 || meta.topicTags.length === 0)
    ) {
      toast.error('Title, at least one tradition, and one topic are required to resubmit.');
      return;
    }
    setSaving(true);
    try {
      const { error } = await supabase
        .from('content')
        .update({
          title: meta.title,
          excerpt: meta.excerpt || null,
          tradition_tags: meta.traditionTags,
          topic_tags: meta.topicTags,
          // Resubmit mirrors the article composer: same row, back to review.
          ...(resubmit ? { status: 'pending_review' } : {}),
        })
        .eq('id', content.id);
      if (error) throw error;
      await catalogMutations.attachCategoryToContent(content.id, meta.categoryId);
      await queryClient.invalidateQueries({ queryKey: ['content', 'id', content.id] });
      await queryClient.invalidateQueries({ queryKey: ['content', 'creator'] });
      toast.success(resubmit ? 'Saved and resubmitted for review.' : 'Saved.');
      if (resubmit) void navigate({ to: '/creator/content' });
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Could not save');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="mx-auto max-w-2xl space-y-5">
      <h1 className="font-serif text-2xl text-ink">Edit video details</h1>

      {status === 'rejected' && content.reviewer_notes && (
        <div className="rounded-md border border-oxblood/30 bg-oxblood/5 p-4">
          <p className="font-sans text-xs uppercase tracking-[0.12em] text-oxblood">
            Editorial notes
          </p>
          <p className="mt-2 font-sans text-sm text-ink/80 pretty">{content.reviewer_notes}</p>
          <p className="mt-2 font-sans text-xs text-ink/60">
            Make your revisions and resubmit — this returns the piece to the review queue.
          </p>
        </div>
      )}

      <input
        value={meta.title}
        onChange={(e) => setMeta((m) => ({ ...m, title: e.target.value }))}
        placeholder="Title"
        className="w-full rounded-md border border-ink/20 bg-parchment px-3 py-2 font-serif text-lg text-ink focus:border-oxblood focus:outline-none"
      />
      <textarea
        value={meta.excerpt}
        onChange={(e) => setMeta((m) => ({ ...m, excerpt: e.target.value }))}
        placeholder="Description"
        rows={3}
        className="w-full rounded-md border border-ink/20 bg-parchment px-3 py-2 text-sm text-ink focus:border-oxblood focus:outline-none"
      />
      <TagRow
        label="Traditions"
        options={TRADITIONS as readonly string[]}
        selected={meta.traditionTags}
        onToggle={(v) => toggle('traditionTags', v)}
      />
      <TagRow
        label="Topics"
        options={TOPICS as readonly string[]}
        selected={meta.topicTags}
        onToggle={(v) => toggle('topicTags', v)}
      />
      <CategoryPicker
        value={meta.categoryId}
        onChange={(categoryId) => setMeta((m) => ({ ...m, categoryId }))}
      />
      <p className="font-sans text-xs text-ink/50">Slug: {content.slug}</p>

      <div className="flex gap-3">
        <button
          type="button"
          disabled={saving}
          onClick={() => void save(false)}
          className="rounded-md border border-ink/20 px-4 py-2 text-sm text-ink hover:border-oxblood disabled:opacity-60"
        >
          Save
        </button>
        {status === 'rejected' && (
          <button
            type="button"
            disabled={saving}
            onClick={() => void save(true)}
            className="rounded-md bg-ink px-4 py-2 text-sm font-medium text-parchment hover:bg-oxblood disabled:opacity-60"
          >
            Save &amp; resubmit
          </button>
        )}
      </div>
    </div>
  );
}

function Note({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="mx-auto max-w-2xl">
      <h1 className="font-serif text-2xl text-ink">{title}</h1>
      <p className="mt-4 font-sans text-sm text-ink/70 pretty">{children}</p>
    </div>
  );
}

function TagRow({
  label,
  options,
  selected,
  onToggle,
}: {
  label: string;
  options: readonly string[];
  selected: string[];
  onToggle: (v: string) => void;
}) {
  return (
    <div>
      <p className="mb-1 font-sans text-xs uppercase tracking-[0.12em] text-ink/60">{label}</p>
      <div className="flex flex-wrap gap-1.5">
        {options.map((o) => {
          const active = selected.includes(o);
          return (
            <button
              key={o}
              type="button"
              onClick={() => onToggle(o)}
              className={`rounded-sm border px-2 py-1 text-xs ${
                active
                  ? 'border-oxblood bg-oxblood/10 text-oxblood'
                  : 'border-ink/15 text-ink/70 hover:border-oxblood'
              }`}
            >
              {DISPLAY[o] ?? o}
            </button>
          );
        })}
      </div>
    </div>
  );
}
